import { Box, Stack, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { COLOR_PRIMARY, COLOR_TERTIARY, CHECKCIRCLE_SIZE } from '../theme'

interface HeroHighlightsPanelProps {
    /** Uppercase eyebrow at the top of the panel (e.g. "Why Core?"). */
    title: string
    /** Primary bullet list — rendered vertically. */
    highlights: string[]
    /** Optional secondary bullets — rendered horizontally below a divider. Hidden when empty. */
    points?: string[]
    /** Colour for the primary bullet check icons. Defaults to the brand gold. */
    highlightColor?: string
    /** Colour for the secondary bullet check icons. Defaults to the brand green. */
    pointColor?: string
    /**
     * Surface context the panel sits on.
     *   'dark'  → glass panel with rgba-white text (hero gradients)
     *   'light' → neutral panel with dark text (white cards / light sections)
     * Defaults to 'dark' to match the original hero use.
     */
    variant?: 'dark' | 'light'
    size?: 'lg' | 'sm'
}

// Palette per surface. Keeping both tables inline so there's one place to tune the look.
const PALETTE = {
    dark: {
        panelBg: 'rgba(255,255,255,0.04)',
        panelBorder: '1px solid rgba(255,255,255,0.1)',
        titleColor: 'rgba(255,255,255,0.45)',
        highlightText: 'rgba(255,255,255,0.75)',
        divider: '1px solid rgba(255,255,255,0.08)',
        pointText: 'rgba(255,255,255,0.55)',
    },
    light: {
        panelBg: 'rgba(0,0,0,0)',
        panelBorder: '1px solid rgba(0,0,0,0)',
        titleColor: 'rgba(0,0,0,0.75)',
        highlightText: 'rgba(0,0,0,0.75)',
        divider: '1px solid rgba(0,0,0,0)',
        pointText: 'rgba(0,0,0,0.55)',
    },
} as const

export function HeroHighlightsPanel({
    title,
    highlights,
    points,
    highlightColor = COLOR_PRIMARY,
    pointColor = COLOR_TERTIARY,
    variant = 'dark',
    size = 'lg'
}: HeroHighlightsPanelProps) {
    const p = PALETTE[variant]
    return (
        <Box sx={{
            background: p.panelBg,
            border: p.panelBorder,
            borderRadius: 3,
            p: size == 'sm' ? 1 : 3,
        }}>
            <Typography variant="caption" sx={{
                color: p.titleColor,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                fontWeight: 600
            }}>
                {title}
            </Typography>
            <Stack spacing={size == 'sm' ? 0.75 : 1.5} sx={{ mt: size == 'sm' ? 1 : 2 }}>
                {highlights.map(h => (
                    <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <CheckCircleIcon sx={{ color: highlightColor, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                        <Typography sx={{ color: p.highlightText, fontSize: '14px' }}>{h}</Typography>
                    </Box>
                ))}
            </Stack>

            {points && points.length > 0 && (
                <Box sx={{ mt: 3, pt: 3, borderTop: p.divider }}>
                    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                        {points.map(text => (
                            <Box key={text} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <CheckCircleIcon sx={{ fontSize: CHECKCIRCLE_SIZE, color: pointColor }} />
                                <Typography variant="caption" sx={{ color: p.pointText }}>{text}</Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    )
}
